import { useMemo } from 'react';
import {SaccoDeposit, SaccoLoanRepayment, SaccoShare, SaccoWithdrawal} from "@/@types/sacco.models.ts";

/** Derived metrics computed client-side from raw sacco data. */
export interface SaccoMetrics {
  totalDeposits: number;
  totalWithdrawals: number;
  totalShareCapital: number;
  outstandingLoanBalance: number;
  loanEligibility: number;
  availableCredit: number;
  netPosition: number;
}

interface UseSaccoMetricsParams {
  deposits: SaccoDeposit[];
  withdrawals: SaccoWithdrawal[];
  shares: SaccoShare[];
  /** All repayments across all loans for this membership (unpaid ones determine outstanding balance). */
  repayments: SaccoLoanRepayment[];
}

/**
 * Pure computation hook — derives SaccoMetrics from raw data.
 * No network calls; re-computes whenever inputs change.
 */
export function useSaccoMetrics({ deposits, withdrawals, shares, repayments }: UseSaccoMetricsParams): SaccoMetrics {
  return useMemo(() => {
    const totalDeposits = deposits.reduce((sum, d) => sum + d.amount, 0);
    const totalWithdrawals = withdrawals.reduce((sum, w) => sum + w.amount, 0);
    const totalShareCapital = shares.reduce((sum, s) => sum + s.units * s.price_per_unit, 0);
    const outstandingLoanBalance = repayments
      .filter((r) => !r.is_paid)
      .reduce((sum, r) => sum + r.installment_amount, 0);

    const netSavings = totalDeposits - totalWithdrawals + totalShareCapital;
    const loanEligibility = 3 * netSavings;
    const availableCredit = Math.max(0, loanEligibility - outstandingLoanBalance);
    const netPosition = netSavings - outstandingLoanBalance;

    return {
      totalDeposits,
      totalWithdrawals,
      totalShareCapital,
      outstandingLoanBalance,
      loanEligibility,
      availableCredit,
      netPosition,
    };
  }, [deposits, withdrawals, shares, repayments]);
}
